/**
 * Computed - A derived reactive value
 *
 * @module @smoothie-framework/ui/state
 */
import { getCurrentObserver, scheduleUpdate, setCurrentObserver } from './context';
import { beginTracking, cleanupObserver, createObservable, createObserver, endTracking, markChanged, needsObserverRefresh, trackDependency, } from './graph';
import { createOwner, getOwner, resetOwner, runWithOwner } from './scope';
import { createPostCommitSubscribers } from './subscriptions';
const REACTION_CLEAN = 0;
const REACTION_MAYBE_DIRTY = 1;
const REACTION_DIRTY = 2;
export function computed(fn) {
    let value;
    let state = REACTION_DIRTY;
    let initialized = false;
    const computeFn = (() => {
        ensureUpToDate();
        const currentObserver = getCurrentObserver();
        trackDependency(currentObserver, observable);
        return value;
    });
    const subscribers = createPostCommitSubscribers(() => computeFn.peek());
    const ensureUpToDate = () => {
        if (state === REACTION_DIRTY) {
            recompute();
            return;
        }
        if (state === REACTION_MAYBE_DIRTY) {
            if (needsObserverRefresh(observer)) {
                recompute();
            }
            else {
                state = REACTION_CLEAN;
            }
        }
    };
    const observable = createObservable({ refresh: ensureUpToDate });
    const invalidate = () => {
        if (state !== REACTION_CLEAN) {
            return;
        }
        state = REACTION_MAYBE_DIRTY;
        for (const obs of observable.observers) {
            scheduleUpdate(obs);
        }
    };
    const observer = createObserver(() => {
        subscribers.notify();
    }, invalidate);
    observer.flushContext = getOwner()?.flushContext ?? observer.flushContext;
    const owner = createOwner(undefined, () => {
        cleanupObserver(observer);
    });
    const recompute = () => {
        const prevObserver = getCurrentObserver();
        const previousValue = value;
        resetOwner(owner);
        beginTracking(observer);
        setCurrentObserver(observer);
        try {
            value = runWithOwner(owner, () => fn());
        }
        finally {
            setCurrentObserver(prevObserver);
            endTracking(observer);
        }
        const changed = !initialized || !Object.is(previousValue, value);
        initialized = true;
        state = REACTION_CLEAN;
        if (changed) {
            markChanged(observable);
        }
    };
    computeFn.peek = () => {
        ensureUpToDate();
        return value;
    };
    computeFn.subscribe = (fn) => {
        return subscribers.subscribe(fn);
    };
    // Add marker for JSX runtime to detect and preserve reactivity
    computeFn.__isComputed = true;
    return computeFn;
}
//# sourceMappingURL=computed.js.map